"use client";

import React, { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Card, Badge, Spinner } from "react-bootstrap";
import { Clock, Eye } from "lucide-react";
import styles from "./BlogCard.module.css";
import { BlogPost } from "@/app/types/blog";
import Image from "next/image";
import { getAuthorAvatar } from "@/app/utils/authorImage";

interface BlogCardProps {
  post: BlogPost;
  index?: number;
}

// Global navigation loading state
let navigationLoading = false;
const listeners = new Set<() => void>();

const setNavigationLoading = (loading: boolean) => {
  navigationLoading = loading;
  listeners.forEach((listener) => listener());
};

export const useNavigationLoading = () => {
  const [loading, setLoading] = useState(navigationLoading);
  const pathname = usePathname();

  useEffect(() => {
    const listener = () => setLoading(navigationLoading);
    listeners.add(listener);

    // Clear loading when pathname changes (navigation completed)
    if (navigationLoading && pathname) {
      setNavigationLoading(false);
    }

    return () => {
      listeners.delete(listener);
    };
  }, [pathname]);

  return loading;
};

export const BlogCard: React.FC<BlogCardProps> = ({ post, index = 0 }) => {
  const isNavigating = useNavigationLoading();

  const slug =
    post.slug ||
    post.title
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

  // ✅ Narrow the author type safely
  const authorName =
    typeof post.author === "object" ? post.author.name : post.author;

  // Use local author images from /public/blog/authorImages/
  const fallbackAvatar =
    typeof post.author === "object"
      ? post.author.avatar
      : post.avatar || "https://i.pravatar.cc/150?img=5";

  const authorAvatar = getAuthorAvatar(authorName, fallbackAvatar);

  const handleLinkClick = () => {
    setNavigationLoading(true);
  };

  const formatViews = (views: number): string => {
    if (views >= 1000000)
      return (views / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
    if (views >= 1000)
      return (views / 1000).toFixed(1).replace(/\.0$/, "") + "k";
    return views.toString();
  };

  const decodeHtmlEntities = (value: string): string =>
    value
      ?.replace(/&amp;/gi, "&")
      .replace(/&lt;/gi, "<")
      .replace(/&gt;/gi, ">")
      .replace(/&quot;/gi, '"')
      .replace(/&#039;/gi, "'")
      .replace(/&ldquo;/gi, "“")
      .replace(/&rdquo;/gi, "”")
      .replace(/&lsquo;/gi, "‘")
      .replace(/&rsquo;/gi, "’") ?? "";

  const categoryLabel = decodeHtmlEntities(post.category || "General");

  return (
    <>
      {isNavigating && (
        <div
          className="loader-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <Spinner
            animation="border"
            variant="primary"
            style={{ width: "3rem", height: "3rem" }}
          />
          <p className="loading-text mt-3">Loading post...</p>
        </div>
      )}
      <Card
        key={post.id}
        className={`${styles.blogCard} border-0 shadow-sm overflow-hidden`}
        style={{ animationDelay: `${index * 0.1}s` }}
      >
        {/* ---------- Thumbnail ---------- */}
        <div className={styles.thumbnailWrapper}>
          <Link
            href={`/blog/${slug}`}
            className="text-decoration-none"
            onClick={handleLinkClick}
          >
            <Card.Img
              src={post.image}
              alt={post.title}
              className={styles.thumbnail}
            />
            <div className={styles.overlay}>
              <span className={styles.overlayText}>Read More</span>
            </div>
          </Link>
        </div>

        {/* ---------- Body ---------- */}
        <div className="p-4 d-flex flex-column">
          <div className="mb-3">
            <Badge className={styles.categoryBadge}>{categoryLabel}</Badge>
          </div>

          <Link
            href={`/blog/${slug}`}
            className="text-decoration-none"
            onClick={handleLinkClick}
          >
            <Card.Title className={`${styles.title} mb-2`}>
              {post.title}
            </Card.Title>
          </Link>

          <Card.Text className={`${styles.excerpt} flex-grow-1`}>
            {post.excerpt}
          </Card.Text>

          {/* ---------- Author + Meta Info ---------- */}
          <div
            className={`${styles.metaInfo} mt-4 d-flex flex-column flex-sm-row justify-content-between align-items-center gap-3`}
          >
            <div className="d-flex align-items-center gap-2">
              <Image
                src={authorAvatar}
                alt={authorName || "Author"}
                className="rounded-circle"
                width={40}
                height={40}
              />
              <span className={styles.authorName}>
                {authorName || "Unknown"}
              </span>
              <span className="text-muted mx-1">•</span>
              <span className={styles.date}>
                {new Date(post.publishedAt).toLocaleDateString()}
              </span>
            </div>

            <div className="d-flex align-items-center gap-3 text-muted small">
              <div className="d-flex align-items-center gap-1">
                <Clock size={14} />
                <span>{post.readTime}</span>
              </div>
              {/* <div className="d-flex align-items-center gap-1">
                <Eye size={14} />
                <span>{formatViews(post.views)}</span>
              </div> */}
            </div>
          </div>
        </div>
      </Card>
    </>
  );
};
